import type { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  type OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from '@fino-ui/button';
import { ModalService } from '@fino-ui/modal';
import { TranslationModule } from '@fino-ui/translation';
import { KeycloakService } from 'keycloak-angular';
import { Subscription } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { ApproveModalComponent } from 'src/app/components/approve-modal/approve-modal.component';
import {
  FileUploadModalComponent,
  UPLOAD_TYPES,
} from 'src/app/components/file-upload-modal/file-upload-modal.component';
import { CachingService } from 'src/app/core/common/caching.service';
import { EnvironmentService } from 'src/app/core/environment/environment.service';
import { ActionBoxComponent } from '../../components/action-box/action-box.component';
import { ActivateProductSwitchComponent } from '../../components/activate-product-switch/activate-product-switch.component';
import { HeaderComponent } from '../../components/header/header.component';
import { ArchivingComponent } from './components/archiving/archiving.component';
import { BasicInfosComponent } from './components/basic-infos/basic-infos.component';
import { KWHDeActivateComponent } from './components/deactivate/deactivate.component';
import { DeadlinesComponent } from './components/deadlines/deadlines.component';
import { FomEmailComponent } from './components/fom-email/fom-email.component';
import { LetterTemplateComponent } from './components/letter-template/letter-template.component';
import { LetterUploadComponent } from './components/letter-upload/letter-upload.component';
import { ARCHIVING_FLOW, type Archiving, type KWHConfig } from './kwh.model';
import { KwhService } from './kwh.service';

export const KWH_MODEL = 'kwhSettings';

@Component({
  selector: 'app-kwh',
  templateUrl: './kwh.component.html',
  styleUrl: './kwh.component.sass',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    HeaderComponent,
    ActivateProductSwitchComponent,
    ActionBoxComponent,
    ArchivingComponent,
    BasicInfosComponent,
    ButtonModule,
    TranslationModule,
  ],
})
export class KwhComponent implements OnInit {
  kwhSettings: KWHConfig;
  FileType = UPLOAD_TYPES;
  @ViewChild('archiving') archivingComponent: ArchivingComponent;

  toggleAutoApplyStandingOrdersSubscription = Subscription.EMPTY;
  toggleStammdatenSubscription = Subscription.EMPTY;

  constructor(
    private kwhService: KwhService,
    private ngxModalService: ModalService,
    private appService: AppService,
    private route: ActivatedRoute,
    private router: Router,
    private authService: KeycloakService,
    private cachingService: CachingService,
    private envService: EnvironmentService,
  ) {}

  ngOnInit(): void {
    this.kwhService.fetchKWH().subscribe((resp: KWHConfig) => {
      this.cacheDataModel(resp);

      this.route.queryParams?.subscribe((p) => {
        if (p.modal) {
          this[p.modal]();
          // remove queryparams silently
          this.router.navigate([], {});
        }
      });
    });
  }

  cacheDataModel(resp: KWHConfig, overwrite = false) {
    this.kwhSettings = this.cachingService.cacheDataModel(
      resp,
      KWH_MODEL,
      overwrite,
    );
  }

  get isArchivingActive() {
    return this.kwhSettings?.archiving?.flow !== ARCHIVING_FLOW.NONE;
  }

  get isActive() {
    return this.kwhSettings?.isAidServiceActive;
  }

  get isAtruvia() {
    return this.appService.isAtruvia;
  }

  get isNotProd() {
    return !this.envService.isProd;
  }

  get showArchiving() {
    return this.isArchivingActive || this.kwhSettings.showArchivingSection;
  }

  get showAutoApplyStandingOrders() {
    return this.isAtruvia;
  }

  get featureAutoApplyStandingOrders() {
    return this.kwhSettings?.autoApplyStandingOrders;
  }

  get featureStammdatenweitergabe() {
    return this.kwhSettings?.getPersondatenFromAtruvia;
  }

  openKWHUserAdministration() {
    this.authService.getToken().then((token) => {
      const link = this.kwhService.getKWHLink(
        token,
        this.kwhSettings.clientUrl,
      );
      window.open(link);
    });
  }

  toggleStandingOrdersSettings() {
    this.ngxModalService
      .addModal<boolean>(ApproveModalComponent, {
        title: 'kwh.actions.autoApplyStandingOrders.title',
        description: `kwh.actions.autoApplyStandingOrders.modal.subtitle.${this.featureAutoApplyStandingOrders ? 'deactive' : 'active'}`,
      })
      .subscribe((approved) => {
        if (approved) {
          this.toggleAutoApplyStandingOrdersSubscription = this.kwhService
            .toggleFeatureAutoApplyStandingOrders(
              !this.featureAutoApplyStandingOrders,
            )
            .subscribe({
              next: () => {
                this.kwhSettings.autoApplyStandingOrders =
                  !this.kwhSettings.autoApplyStandingOrders;
                this.cacheDataModel(this.kwhSettings, true);

                this.appService.success(
                  'kwh.actions.autoApplyStandingOrders.success',
                );
              },
              error: () => {
                this.appService.error(
                  'kwh.actions.autoApplyStandingOrders.error',
                );
              },
            });
        }
      });
  }

  toggleStammdatenSettings() {
    this.ngxModalService
      .addModal<boolean>(ApproveModalComponent, {
        title: 'kwh.actions.stammdaten.title',
        description: `kwh.actions.stammdaten.modal.subtitle.${this.featureStammdatenweitergabe ? 'deactive' : 'active'}`,
      })
      .subscribe((approved) => {
        if (approved) {
          this.toggleStammdatenSubscription = this.kwhService
            .toggleFeatureGetPersonDataFromAtruvia(
              !this.featureStammdatenweitergabe,
            )
            .subscribe({
              next: () => {
                this.kwhSettings.getPersondatenFromAtruvia =
                  !this.kwhSettings.getPersondatenFromAtruvia;

                this.cacheDataModel(this.kwhSettings, true);

                this.appService.success('kwh.actions.stammdaten.success');
              },
              error: () => {
                this.appService.error('kwh.actions.stammdaten.error');
              },
            });
        }
      });
  }

  toggleActiveState($event) {
    const approved = this.ngxModalService.addModal<boolean>(
      KWHDeActivateComponent,
      {
        isAidServiceActive: this.kwhSettings.isAidServiceActive,
      },
    );
    this.kwhSettings.isAidServiceActive = $event;

    approved.subscribe((result) => {
      if (!result) {
        this.kwhSettings.isAidServiceActive = !$event;
        return;
      }

      const request = $event
        ? this.kwhService.activateKWHService()
        : this.kwhService.deactivateKWHService();

      request.subscribe({
        next: () => {
          this.appService.success(
            `kwh.${$event ? 'activate' : 'deactivate'}.success`,
          );
        },
        error: () => {
          this.appService.error(
            `kwh.${$event ? 'activate' : 'deactivate'}.error`,
          );
          this.kwhSettings.isAidServiceActive = !$event;
        },
      });
    });
  }

  openDeadlinesModal() {
    this.ngxModalService
      .addModal<number>(DeadlinesComponent, {
        deadline:
          this.kwhSettings.noReplyAfterWarrantSentToTransferring.bankingDays,
      })
      .subscribe((result) => {
        if (result) {
          this.kwhService.changeKWHDeadline(result).subscribe({
            next: () => {
              this.cacheDataModel(
                {
                  ...this.kwhSettings,
                  noReplyAfterWarrantSentToTransferring: {
                    bankingDays: result,
                  },
                },
                true,
              );
              this.appService.success('kwh.actions.mahnversand.success');
            },
            error: () => {
              this.appService.error('kwh.actions.mahnversand.error');
            },
          });
        }
      });
  }

  openLetterTemplateModal() {
    const { templateSenderAddress } = this.kwhSettings;
    this.ngxModalService
      .addModal(LetterTemplateComponent, {
        faxNumber: templateSenderAddress.faxNumber,
        phoneNumber: templateSenderAddress.phoneNumber,
        department: templateSenderAddress.department,
        city: templateSenderAddress.city,
        address: templateSenderAddress.address,
        postcode: templateSenderAddress.postcode,
        displayName: templateSenderAddress.displayName,
      })
      .subscribe((result: any) => {
        if (result) {
          this.kwhService.letterHead(result).subscribe({
            next: () => {
              this.cacheDataModel(
                this.kwhService.patchTemplateSenderAddress(
                  this.kwhSettings,
                  result,
                ),
                true,
              );
              this.appService.success('kwh.actions.letterHead.success');
            },
            error: () => {
              this.appService.error('kwh.actions.letterHead.error');
            },
          });
        }
      });
  }

  openLetterUploadModal() {
    this.ngxModalService
      .addModal<string>(FileUploadModalComponent, {
        title: 'kwh.letterTemplateUpload.title',
        description: 'kwh.letterTemplateUpload.description',
        hidePatientHint: true,
        fileType: UPLOAD_TYPES.PDF,
      })
      .subscribe((result) => {
        if (result) {
          this.kwhService.letterTemplatePreview(result).subscribe({
            next: (res) => {
              this.ngxModalService
                .addModal<string>(LetterUploadComponent, {
                  pdfPreview: res,
                })
                .subscribe((res) => {
                  if (res) {
                    this.kwhService.letterTemplate(result).subscribe({
                      next: (res) => {
                        this.appService.success(
                          'kwh.actions.letterTemplate.success',
                        );
                      },
                      error: (res) => {
                        this.appService.error(
                          'kwh.actions.letterTemplate.error',
                        );
                      },
                    });
                  }
                });
            },
            error: (err: HttpErrorResponse) => {
              this.appService.error(
                `kwh.actions.letterTemplatePreview.error.${err.status}`,
              );
            },
          });
        }
      });
  }

  openFomEmailModal() {
    this.ngxModalService
      .addModal<string>(FomEmailComponent, {
        fomEmail: this.kwhSettings.fomEmail,
      })
      .subscribe((result) => {
        if (result) {
          this.kwhService
            .changeableBankDetails({ fomEmail: result })
            .subscribe({
              next: () => {
                this.cacheDataModel(
                  {
                    ...this.kwhSettings,
                    fomEmail: result,
                  },
                  true,
                );
                this.appService.success('kwh.actions.fomEmail.success');
              },
              error: () => {
                this.appService.error('kwh.actions.fomEmail.error');
              },
            });
        }
      });
  }

  archivingChanged(archiving: Archiving) {
    this.cacheDataModel({ ...this.kwhSettings, archiving }, true);
  }

  openArchivingModal() {
    setTimeout(() => {
      this.archivingComponent?.openModal();
    });
  }
}
